import axios from 'axios';
import { attachOfflineInterceptor } from './offlineSync';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Attach offline sync (must be BEFORE the 401/402 interceptor so queued 202 short-circuits)
attachOfflineInterceptor(api);

// Purge SW API cache after every successful mutation so subsequent GETs are always fresh
const purgeSwApiCache = () => {
  try {
    if (!('serviceWorker' in navigator)) return;
    const controller = navigator.serviceWorker.controller;
    if (controller) {
      controller.postMessage({ type: 'PURGE_API_CACHE' });
      return;
    }
    // Fallback : premier chargement, pas encore controllé — attendre ready et poster à l'active
    navigator.serviceWorker.ready.then((reg) => {
      if (reg.active) reg.active.postMessage({ type: 'PURGE_API_CACHE' });
    }).catch(() => {});
  } catch (_) { /* noop */ }
};

api.interceptors.response.use(
  (response) => {
    try {
      const method = (response.config?.method || 'get').toLowerCase();
      const isWrite = ['post', 'put', 'patch', 'delete'].includes(method);
      const isOfflineQueued = response.status === 202 && response.data?.queued;
      if (isWrite && !isOfflineQueued) purgeSwApiCache();
    } catch (_) { /* noop */ }
    return response;
  }
);

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors + subscription expired (402)
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    if (error.response?.status === 402) {
      // Subscription expired - emit global event
      const detail = error.response?.data?.detail;
      try {
        window.dispatchEvent(new CustomEvent('subscription-expired', {
          detail: typeof detail === 'object' ? detail : { message: detail }
        }));
      } catch (e) { /* noop */ }
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (data) => api.post('/auth/login', data),
  register: (data) => api.post('/auth/register', data),
  getMe: () => api.get('/auth/me'),
};

// Properties API
export const propertiesAPI = {
  getAll: (params) => api.get('/properties', { params }),
  getById: (id) => api.get(`/properties/${id}`),
  create: (data) => api.post('/properties', data),
  update: (id, data) => api.put(`/properties/${id}`, data),
  delete: (id) => api.delete(`/properties/${id}`),
  // Units
  getUnits: (propertyId) => api.get(`/properties/${propertyId}/units`),
  createUnit: (propertyId, data) => api.post(`/properties/${propertyId}/units`, data),
  updateUnit: (propertyId, unitId, data) => api.put(`/properties/${propertyId}/units/${unitId}`, data),
  deleteUnit: (propertyId, unitId) => api.delete(`/properties/${propertyId}/units/${unitId}`),
  assignTenant: (propertyId, unitId, tenantId, leaseDate) => 
    api.post(`/properties/${propertyId}/units/${unitId}/assign-tenant?tenant_id=${tenantId}&lease_start_date=${leaseDate}`),
  releaseUnit: (propertyId, unitId) => api.post(`/properties/${propertyId}/units/${unitId}/release`),
  getAvailableUnits: () => api.get('/available-units'),
  // Mandate PDF
  downloadMandate: (id) => api.get(`/properties/${id}/mandate/pdf`, { responseType: 'blob' }),
  // Mandate e-signature link
  createSignatureLink: (id) => api.post(`/properties/${id}/mandate/signature-link`),
  // Mandate agency signature
  signMandateAsAgency: (id, data) => api.post(`/properties/${id}/mandate/agency-sign`, data),
  clearAgencySignature: (id) => api.delete(`/properties/${id}/mandate/agency-sign`),
  // Mandate termination
  terminateMandate: (id, data) => api.put(`/properties/${id}/terminate-mandate`, data),
  reactivateMandate: (id) => api.put(`/properties/${id}/reactivate-mandate`),
  downloadTerminationCertificate: (id) => api.get(`/properties/${id}/termination-certificate/pdf`, { responseType: 'blob' }),
  // Financial statement
  getFinancialStatement: (id, year) => api.get(`/properties/${id}/financial-statement/${year}`),
  downloadFinancialStatement: (id, year) => api.get(`/properties/${id}/financial-statement/${year}/pdf`, { responseType: 'blob' }),
  // Owner balance & withdrawals
  getOwnerBalance: (id) => api.get(`/properties/${id}/owner-balance`),
  getWithdrawals: (id) => api.get(`/properties/${id}/withdrawals`),
  createWithdrawal: (id, data) => api.post(`/properties/${id}/withdraw`, data),
};

// Withdrawals API
export const withdrawalsAPI = {
  downloadReceipt: (id) => api.get(`/withdrawals/${id}/pdf`, { responseType: 'blob' }),
  delete: (id) => api.delete(`/withdrawals/${id}`),
};

// Security Deposits API (Dépôts de garantie)
export const securityDepositsAPI = {
  getAll: (params) => api.get('/security-deposits', { params }),
  getById: (id) => api.get(`/security-deposits/${id}`),
  create: (data) => api.post('/security-deposits', data),
  update: (id, data) => api.put(`/security-deposits/${id}`, data),
  restitute: (id, amount, notes) => api.put(`/security-deposits/${id}/restitute`, null, { 
    params: { restitution_amount: amount, notes } 
  }),
  delete: (id) => api.delete(`/security-deposits/${id}`),
  downloadPdf: (id) => api.get(`/security-deposits/${id}/pdf`, { responseType: 'blob' }),
};

// Lease End Invoices API (Facturation fin de bail déductible du dépôt)
export const leaseEndInvoicesAPI = {
  getAll: (params) => api.get('/lease-end-invoices', { params }),
  getById: (id) => api.get(`/lease-end-invoices/${id}`),
  create: (data) => api.post('/lease-end-invoices', data),
  update: (id, data) => api.put(`/lease-end-invoices/${id}`, data),
  validate: (id) => api.post(`/lease-end-invoices/${id}/validate`),
  delete: (id) => api.delete(`/lease-end-invoices/${id}`),
  downloadPdf: (id) => api.get(`/lease-end-invoices/${id}/pdf`, { responseType: 'blob' }),
};

// Rent Increases API (Modifications de loyer + courrier + avenant)
export const rentIncreasesAPI = {
  getAll: (params) => api.get('/rent-increases', { params }),
  getById: (id) => api.get(`/rent-increases/${id}`),
  create: (data) => api.post('/rent-increases', data),
  update: (id, data) => api.put(`/rent-increases/${id}`, data),
  notify: (id) => api.post(`/rent-increases/${id}/notify`),
  apply: (id) => api.post(`/rent-increases/${id}/apply`),
  delete: (id) => api.delete(`/rent-increases/${id}`),
  downloadPdf: (id) => api.get(`/rent-increases/${id}/pdf`, { responseType: 'blob' }),
  downloadAvenant: (id) => api.get(`/rent-increases/${id}/avenant-pdf`, { responseType: 'blob' }),
  uploadSignedAvenant: (id, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/rent-increases/${id}/signed-avenant`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deleteSignedAvenant: (id) => api.delete(`/rent-increases/${id}/signed-avenant`),
};

// Clients API
export const clientsAPI = {
  getAll: (params) => api.get('/clients', { params }),
  getById: (id) => api.get(`/clients/${id}`),
  create: (data) => api.post('/clients', data),
  update: (id, data) => api.put(`/clients/${id}`, data),
  delete: (id) => api.delete(`/clients/${id}`),
  downloadWelcomeLetter: (id) => api.get(`/clients/${id}/welcome-letter/pdf`, { responseType: 'blob' }),
  scanId: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/clients/scan-id', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000,
    });
  },
  // Lease termination
  getLeaseStatus: (id) => api.get(`/clients/${id}/lease-status`),
  terminateLease: (id, data) => api.put(`/clients/${id}/terminate-lease`, data),
  reactivateLease: (id) => api.put(`/clients/${id}/reactivate-lease`),
  downloadLeaseTermination: (id) => api.get(`/clients/${id}/lease-termination/pdf`, { responseType: 'blob' }),
  // Identity documents
  getIdentityDocs: (id) => api.get(`/clients/${id}/identity-docs`),
  uploadIdentityDoc: (id, formData) => api.post(`/clients/${id}/identity-docs`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  deleteIdentityDoc: (clientId, docId) => api.delete(`/clients/${clientId}/identity-docs/${docId}`),
  printIdentityDocs: (id) => api.get(`/clients/${id}/identity-docs/pdf`, { responseType: 'blob' }),
};

// Transactions API
export const transactionsAPI = {
  getAll: (params) => api.get('/transactions', { params }),
  getById: (id) => api.get(`/transactions/${id}`),
  create: (data) => api.post('/transactions', data),
  update: (id, data) => api.put(`/transactions/${id}`, data),
  updateLeaseDates: (id, data) => api.put(`/transactions/${id}/lease-dates`, data),
  delete: (id) => api.delete(`/transactions/${id}`),
  downloadLeaseContract: (id, extraParams = {}) => api.get(`/transactions/${id}/lease-contract-pdf`, {
    responseType: 'blob',
    params: extraParams,
    // Defeat any HTTP cache layer
    headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' },
  }),
  uploadSignedContract: (id, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/transactions/${id}/signed-contract`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deleteSignedContract: (id) => api.delete(`/transactions/${id}/signed-contract`),
};

// Activity Logs API (Traçabilité)
export const activityLogsAPI = {
  getAll: (params) => api.get('/activity-logs', { params }),
  getByResource: (resourceType, resourceId) => api.get(`/activity-logs/resource/${resourceType}/${resourceId}`),
};

// Collaborators API
export const collaboratorsAPI = {
  getAll: () => api.get('/collaborators'),
  create: (data) => api.post('/collaborators', data),
  update: (id, data) => api.put(`/collaborators/${id}`, data),
  delete: (id) => api.delete(`/collaborators/${id}`),
  reactivate: (id) => api.put(`/collaborators/${id}/reactivate`),
};

// Agency Settings API
export const agencySettingsAPI = {
  get: () => api.get('/agency-settings'),
  update: (data) => api.put('/agency-settings', data),
  getPublic: (agencyId) => axios.get(`${API_BASE}/agency-settings/public${agencyId ? `?agency_id=${encodeURIComponent(agencyId)}` : ''}`),
  // Endpoints authentifies pour multi-agence (le token contient agency_id)
  getForTenant: () => {
    const tk = localStorage.getItem('tenant_token');
    return axios.get(`${API_BASE}/tenant/agency-settings`, { headers: { Authorization: `Bearer ${tk}` } });
  },
  getForOwner: () => {
    const tk = localStorage.getItem('owner_token');
    return axios.get(`${API_BASE}/owner/agency-settings`, { headers: { Authorization: `Bearer ${tk}` } });
  },
  uploadLogo: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/upload/logo', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  }
};

// Payment APIs (per-agency credentials)
export const paymentApisAPI = {
  getCatalog: () => api.get('/payment-apis/catalog'),
  list: () => api.get('/payment-apis'),
  save: (provider, data) => api.put(`/payment-apis/${provider}`, data),
  remove: (provider) => api.delete(`/payment-apis/${provider}`),
  toggle: (provider, is_active) => api.post(`/payment-apis/${provider}/toggle`, { is_active }),
};

// Dashboard API
export const dashboardAPI = {
  getStats: () => api.get('/dashboard/stats'),
};

// Agency Balance API (Bilan Agence)
export const agencyBalanceAPI = {
  getBalance: (year) => api.get('/agency-balance', { params: year ? { year } : {} }),
  downloadPdf: (year) => api.get('/agency-balance/pdf', { 
    params: year ? { year } : {},
    responseType: 'blob' 
  }),
};

// Utility API
export const utilityAPI = {
  getDistricts: () => api.get('/districts'),
};

// ============ TENANT PORTAL API ============

// Create separate axios instance for tenant portal
const tenantApi = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add tenant token to requests
tenantApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('tenant_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle tenant auth errors
tenantApi.interceptors.response.use(
  (response) => {
    // Purge SW API cache after every successful mutation
    try {
      const method = (response.config?.method || 'get').toLowerCase();
      const isWrite = ['post', 'put', 'patch', 'delete'].includes(method);
      if (isWrite) purgeSwApiCache();
    } catch (_) { /* noop */ }
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('tenant_token');
      localStorage.removeItem('tenant_data');
      window.location.href = '/tenant/login';
    }
    return Promise.reject(error);
  }
);

export const tenantAPI = {
  // Login without auth
  login: (data) => axios.post(`${API_BASE}/tenant/login`, data),
  loginWithCredentials: (data) => axios.post(`${API_BASE}/tenant/login-credentials`, data),
  loginWithPin: (data) => axios.post(`${API_BASE}/tenant/login-pin`, data),
  getAvailableProperties: (phone) => axios.get(`${API_BASE}/tenant/available-properties`, { params: { phone } }),
  
  // Protected routes
  getReceipts: () => tenantApi.get('/tenant/receipts'),
  getReceipt: (id) => tenantApi.get(`/tenant/receipt/${id}`),
  getProfile: () => tenantApi.get('/tenant/profile'),
  
  // PDF downloads
  downloadReceiptPdf: (id) => tenantApi.get(`/tenant/receipt/${id}/pdf`, { responseType: 'blob' }),
  
  // Due notices
  getDueNotices: () => tenantApi.get('/tenant/due-notices'),
  downloadDueNoticePdf: (id) => tenantApi.get(`/tenant/due-notices/${id}/pdf`, { responseType: 'blob' }),
  
  // Security Deposits (Dépôts de garantie)
  getSecurityDeposits: () => tenantApi.get('/tenant/security-deposits'),
  downloadSecurityDepositPdf: (id) => tenantApi.get(`/tenant/security-deposits/${id}/pdf`, { responseType: 'blob' }),
  
  // Lease Contract (Contrat de location)
  downloadLeaseContract: () => tenantApi.get('/tenant/lease-contract', { responseType: 'blob' }),
  getSignedContractInfo: () => tenantApi.get('/tenant/signed-contract-info'),

  // Payment Methods (Fiche des modes de paiement)
  downloadPaymentMethodsPdf: () => tenantApi.get('/tenant/payment-methods/pdf', { responseType: 'blob' }),

  // Rent Increases / Avenants visible by tenant
  getRentIncreases: () => tenantApi.get('/tenant/rent-increases'),
  downloadAvenantPdf: (id) => tenantApi.get(`/tenant/rent-increases/${id}/avenant-pdf`, { responseType: 'blob' }),
  downloadAvenantLetterPdf: (id) => tenantApi.get(`/tenant/rent-increases/${id}/letter-pdf`, { responseType: 'blob' }),

  // Nouveau : paiement unifié (Stripe / CinetPay / Manuel mobile money)
  getAvailablePaymentMethods: () => tenantApi.get('/tenant/payment/methods-available'),
  initiatePayment: (data) => tenantApi.post('/tenant/payment/initiate', data),
  getPaymentStatus: (txId) => tenantApi.get(`/tenant/payment/status/${txId}`),
  getStripeSessionStatus: (sessionId) => tenantApi.get(`/tenant/payment/stripe/status/${sessionId}`),

  // Residence certificate (Attestation de domicile)
  downloadResidenceCertificate: () => tenantApi.get('/tenant/residence-certificate/pdf', { responseType: 'blob' }),
  
  // Requests
  getRequests: () => tenantApi.get('/tenant/requests'),
  getRequest: (id) => tenantApi.get(`/tenant/requests/${id}`),
  createRequest: (data) => tenantApi.post('/tenant/requests', data),
  uploadRequestPhoto: (formData) => tenantApi.post('/tenant/requests/photos', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  
  // Orange Money Payments
  getPaymentConfig: () => tenantApi.get('/tenant/payment/config'),
  initiateOrangeMoneyPayment: (data) => tenantApi.post('/tenant/payment/orange-money/initiate', data),
  confirmOrangeMoneyPayment: (data) => tenantApi.post('/tenant/payment/orange-money/confirm', data),
  cancelOrangeMoneyPayment: (transactionId) => tenantApi.post(`/tenant/payment/orange-money/cancel?transaction_id=${transactionId}`),
  getPaymentHistory: () => tenantApi.get('/tenant/payment/history'),

  // Jemenipay (Mobile Money Orange/Moov) real integration
  getJemeniConfig: () => tenantApi.get('/tenant/payment/jemeni/config'),
  initiateJemeniPayment: (data) => tenantApi.post('/tenant/payment/jemeni/initiate', data),
  getJemeniStatus: (transactionId) => tenantApi.get(`/tenant/payment/jemeni/status/${transactionId}`),

  // Signatures
  saveSignature: (data) => tenantApi.post('/tenant/signatures', data),
  getSignatures: (docType, docId) => tenantApi.get(`/tenant/signatures/${docType}/${docId}`),
  
  // Anomalies
  reportAnomaly: (data) => tenantApi.post('/tenant/report-anomaly', data),
  getMyAnomalies: () => tenantApi.get('/tenant/my-anomalies'),

  // Tenant documents (Remises, Etats des lieux)
  getKeyHandovers: () => tenantApi.get('/tenant/key-handovers'),
  getInspections: () => tenantApi.get('/tenant/inspections'),
  getRepairs: () => tenantApi.get('/tenant/repairs'),
};

// Tenant Accounts API (Admin)
export const tenantAccountsAPI = {
  getAll: (params) => api.get('/tenant-accounts', { params }),
  get: (id) => api.get(`/tenant-accounts/${id}`),
  create: (data) => api.post('/tenant-accounts', data),
  update: (id, data) => api.put(`/tenant-accounts/${id}`, data),
  delete: (id) => api.delete(`/tenant-accounts/${id}`),
  generatePin: (id) => api.post(`/tenant-accounts/${id}/generate-pin`),
};

// Admin Tenant Requests API
export const tenantRequestsAPI = {
  getAll: (params) => api.get('/admin/tenant-requests', { params }),
  respond: (id, data) => api.put(`/admin/tenant-requests/${id}`, data),
};

// Due Notices API (Admin)
export const dueNoticesAPI = {
  getAll: (params) => api.get('/due-notices', { params }),
  create: (data) => api.post('/due-notices', data),
  update: (id, data) => api.put(`/due-notices/${id}`, data),
  delete: (id) => api.delete(`/due-notices/${id}`),
  downloadPdf: (id) => api.get(`/due-notices/${id}/pdf`, { responseType: 'blob' }),
  markPaid: (id, data) => api.post(`/due-notices/${id}/mark-paid`, data || {}),
  generate: (month) => api.post('/due-notices/generate', null, { params: month ? { month } : {} }),
  generationStatus: () => api.get('/due-notices/generation-status'),
  generateForMonth: (month) => api.post('/due-notices/generate', null, { params: month ? { month } : {} }),
};

// Rent Payments API with confirmation
export const rentPaymentsAPI = {
  getAll: (params) => api.get('/rent-payments', { params }),
  getById: (id) => api.get(`/rent-payments/${id}`),
  create: (data) => api.post('/rent-payments', data),
  createHistorical: (data) => api.post('/rent-payments/historical', data),
  update: (id, data) => api.put(`/rent-payments/${id}`, data),
  delete: (id) => api.delete(`/rent-payments/${id}`),
  downloadPDF: (id) => api.get(`/rent-payments/${id}/pdf`, { responseType: 'blob' }),
  getPending: () => api.get('/rent-payments/pending'),
  confirm: (id) => api.put(`/rent-payments/${id}/confirm`),
  cancel: (id, reason) => api.put(`/rent-payments/${id}/cancel`, { reason }),
  reinstate: (id) => api.put(`/rent-payments/${id}/reinstate`),
  getExpectedAmount: (params) => api.get('/rent-payments/expected-amount', { params }),
};

// Parcel Verifications API (Verification de papiers d'un terrain/parcelle)
export const parcelVerificationsAPI = {
  getAll: (params) => api.get('/parcel-verifications', { params }),
  getById: (id) => api.get(`/parcel-verifications/${id}`),
  create: (data) => api.post('/parcel-verifications', data),
  update: (id, data) => api.put(`/parcel-verifications/${id}`, data),
  delete: (id) => api.delete(`/parcel-verifications/${id}`),
  uploadDocument: (id, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/parcel-verifications/${id}/documents`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deleteDocument: (id, fileId) => api.delete(`/parcel-verifications/${id}/documents/${fileId}`),
  downloadReportPdf: (id) => api.get(`/parcel-verifications/${id}/report-pdf`, { responseType: 'blob' }),
  downloadRequestPdf: (id) => api.get(`/parcel-verifications/${id}/request-pdf`, { responseType: 'blob' }),
  downloadCessionDeedPdf: (id) => api.get(`/parcel-verifications/${id}/cession-deed-pdf`, { responseType: 'blob' }),
  saveCessionSignatures: (id, payload) => api.post(`/parcel-verifications/${id}/cession-signatures`, payload),
};

// Rent Payments PDF
export const rentPaymentsPdfAPI = {
  downloadPdf: (id) => api.get(`/rent-payments/${id}/pdf`, { responseType: 'blob' }),
};

// Land Sales API (Ventes de terrains)
export const landSalesAPI = {
  getAll: (params) => api.get('/land-sales', { params }),
  getById: (id) => api.get(`/land-sales/${id}`),
  create: (data) => api.post('/land-sales', data),
  update: (id, data) => api.put(`/land-sales/${id}`, data),
  delete: (id) => api.delete(`/land-sales/${id}`),
  getPayments: (id) => api.get(`/land-sales/${id}/payments`),
  addPayment: (saleId, data) => api.post(`/land-sales/${saleId}/payments`, data),
  downloadPaymentPdf: (paymentId) => api.get(`/land-payments/${paymentId}/pdf`, { responseType: 'blob' }),
};

// Property Inspections API (États des lieux)
export const propertyInspectionsAPI = {
  getAll: (params) => api.get('/property-inspections', { params }),
  getById: (id) => api.get(`/property-inspections/${id}`),
  create: (data) => api.post('/property-inspections', data),
  update: (id, data) => api.put(`/property-inspections/${id}`, data),
  delete: (id) => api.delete(`/property-inspections/${id}`),
  getStandardItems: () => api.get('/property-inspections/standard-items'),
  downloadPdf: (id) => api.get(`/property-inspections/${id}/pdf`, { responseType: 'blob' }),
};

// Key Handovers API (Remise de Clés)
export const keyHandoversAPI = {
  getAll: (params) => api.get('/key-handovers', { params }),
  getById: (id) => api.get(`/key-handovers/${id}`),
  create: (data) => api.post('/key-handovers', data),
  update: (id, data) => api.put(`/key-handovers/${id}`, data),
  delete: (id) => api.delete(`/key-handovers/${id}`),
  downloadPdf: (id) => api.get(`/key-handovers/${id}/pdf`, { responseType: 'blob' }),
};

// Property Visits API (Visites de biens)
export const propertyVisitsAPI = {
  getAll: (params) => api.get('/property-visits', { params }),
  getById: (id) => api.get(`/property-visits/${id}`),
  create: (data) => api.post('/property-visits', data),
  update: (id, data) => api.put(`/property-visits/${id}`, data),
  delete: (id) => api.delete(`/property-visits/${id}`),
  addAnomaly: (visitId, data) => api.post(`/property-visits/${visitId}/anomalies`, data),
  updateAnomaly: (visitId, anomalyId, status) => api.put(`/property-visits/${visitId}/anomalies/${anomalyId}?status=${status}`),
  uploadAnomalyPhotos: (visitId, anomalyId, formData) => api.post(`/property-visits/${visitId}/anomalies/${anomalyId}/photos`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  deleteAnomalyPhoto: (visitId, anomalyId, photoUrl) => api.delete(`/property-visits/${visitId}/anomalies/${anomalyId}/photos?photo_url=${encodeURIComponent(photoUrl)}`),
  getCategories: () => api.get('/property-visits/categories'),
  downloadPdf: (id) => api.get(`/property-visits/${id}/pdf`, { responseType: 'blob' }),
};

// Signatures API (Admin)
export const signaturesAPI = {
  save: (data) => api.post('/signatures', data),
  get: (docType, docId) => api.get(`/signatures/${docType}/${docId}`),
  getSummary: () => api.get('/signatures/summary'),
};

// Repair Requests API (workflow validation admin)
export const repairRequestsAPI = {
  getMeta: () => api.get('/repair-requests/meta'),
  getAll: (params) => api.get('/repair-requests', { params }),
  getById: (id) => api.get(`/repair-requests/${id}`),
  create: (data) => api.post('/repair-requests', data),
  approve: (id, data) => api.post(`/repair-requests/${id}/approve`, data),
  reject: (id, reason) => api.post(`/repair-requests/${id}/reject`, { reason }),
  uploadPhoto: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/repair-requests/photos/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
};

// Agency Expenses API (Frais d'agence)
export const agencyExpensesAPI = {
  getCategories: () => api.get('/agency-expenses/categories'),
  getAll: (params) => api.get('/agency-expenses', { params }),
  getById: (id) => api.get(`/agency-expenses/${id}`),
  create: (formData) => api.post('/agency-expenses', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  update: (id, data) => api.put(`/agency-expenses/${id}`, data),
  delete: (id) => api.delete(`/agency-expenses/${id}`),
  validate: (id, note) => api.post(`/agency-expenses/${id}/validate`, { note: note || null }),
  reject: (id, reason) => api.post(`/agency-expenses/${id}/reject`, { reason }),
  uploadInvoice: (id, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/agency-expenses/${id}/invoice`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  deleteInvoice: (id) => api.delete(`/agency-expenses/${id}/invoice`),
  getStats: (year) => api.get('/agency-expenses/stats', { params: year ? { year } : {} }),
  downloadStatsPdf: (year) => api.get('/agency-expenses/stats/pdf', { params: year ? { year } : {}, responseType: 'blob' }),
};

// Keys Inventory API (Stock de cles)
export const keysInventoryAPI = {
  getMeta: () => api.get('/keys/types'),
  getNextReference: (propertyId) => api.get('/keys/next-reference', { params: { property_id: propertyId } }),
  getAll: (params) => api.get('/keys', { params }),
  getById: (id) => api.get(`/keys/${id}`),
  getByProperty: (propertyId) => api.get('/keys', { params: { property_id: propertyId } }),
  getByClient: (clientId) => api.get(`/keys/by-client/${clientId}`),
  create: (data) => api.post('/keys', data),
  update: (id, data) => api.put(`/keys/${id}`, data),
  delete: (id) => api.delete(`/keys/${id}`),
  changeStatus: (id, payload) => api.post(`/keys/${id}/status`, payload),
  getTimeline: (id) => api.get(`/keys/${id}/timeline`),
};

// Subscriptions API (SaaS)
export const subscriptionsAPI = {
  getMy: () => api.get('/subscription/my'),
  getPlans: () => api.get('/subscription-plans'),
  createCheckout: (plan, originUrl) => api.post('/subscriptions/checkout', { plan, origin_url: originUrl }),
  getPaymentInfo: () => api.get('/subscriptions/payment-info'),
  notifyMobilePayment: (data) => api.post('/subscriptions/notify-mobile-payment', data),
  // Mobile money declarations (super-admin)
  listMobilePayments: (status) => api.get('/admin/mobile-payments', { params: status ? { status } : {} }),
  approveMobilePayment: (id) => api.post(`/admin/mobile-payments/${id}/approve`),
  rejectMobilePayment: (id, reason) => api.post(`/admin/mobile-payments/${id}/reject`, { reason }),
};

export default api;
